<template lang="pug">
    span(
        role="combobox",
        class="_drop",
        :class="_class",
        :style="{ width, height }",
        :id="_id",
        :aria-expanded="expanded",
        :aria-owns="_iid",
        v-on="__fwd")
        template(v-if="multiple", v-for="(o,i) in selectedOptions")
            ib-tag(:disabled="disabled", @delete="_rmtag(o)") {{o.textContent}}
            | {{ filtered || i < selectedOptions.length-1 ? '&thinsp;' : null }}
        span(class="_fldi") &zwnj;
            label(
                :for="_iid",
                class="_fldl",
                :class="{ _dock: dock }") {{ placeholder }}
            input(
                v-if="filtered",
                v-model="filter",
                ref="_in",
                class="_fldin",
                :id="_iid",
                :name="name",
                :required="required",
                :readonly="readonly",
                :disabled="disabled",
                :inert="inert",
                :tabindex="inert ? -1 : tabindex",
                :aria-controls="_id",
                autocomplete="new-password",
                autocorrect="off",
                autocapitalize="off",
                v-on="__on",
                @keydown="_keydown",
                @input="input")
            span(
                v-else-if="!native",
                v-html="!multiple && selectedOptions.length ? selectedOptions[0].innerHTML : placeholder ? $string.escapeHTML(placeholder) : '&zwnj;'",
                ref="_in",
                class="_fldis",
                :class="{ _dock: !dock }",
                :tabindex="inert || disabled ? null : (tabindex || 0)",
                v-on="__on",
                @keydown="_keydown")

            // Native picker - TODO:REMOVE
            select(v-else,
                ref="_in",
                class="_fldin",
                size="1",
                :id="_iid",
                :name="name",
                :multiple="multiple",
                :required="required",
                :disabled="disabled",
                :readonly="readonly",
                :tabindex="inert ? -1 : tabindex",
                v-on="__on",
                @change="_change")
                template(v-for="opt in data")
                    optgroup(v-if="opt.child", :label="opt.label")
                        option(v-for="sopt in opt.child",
                            :disabled="getdisabled(sopt)",
                            :value="getval(sopt)")
                            slot(v-bind="sopt") {{typeof sopt === 'object' ? sopt.label : sopt}}
                    option(v-else,
                        :disabled="getdisabled(opt)",
                        :value="getval(opt)")
                        slot(v-bind="opt") {{typeof opt === 'object' ? opt.label : opt}}

        // Emulated picker
        ul(v-if="!native", ref="ul", role="listbox", class="_ddl")
            template(v-for="opt in data")
                template(v-if="opt.child")
                    li
                        slot(v-bind="opt") {{opt.label}}
                    li(v-for="sopt in opt.child",
                        role="option",
                        :aria-disabled="getdisabled(sopt)",
                        :data-value="getval(sopt)")
                        slot(v-bind="sopt") {{typeof sopt === 'object' ? sopt.label : sopt}}
                template(v-else)
                    li(
                        role="option",
                        :aria-disabled="getdisabled(opt)",
                        :data-value="getval(opt)")
                        slot(v-bind="opt") {{typeof opt === 'object' ? opt.label : opt}}
</template>

<script>
    import $device from '../../global/device';
    import entry from './ib-form-entry.vue';
    import picker from './ib-dropdown-pick';

    // older browsers have buggy or missing implementation of selectedOptions
    const haveSelectedOptions = platform =>
          platform == 'android' ? true
        : platform == 'ios' ? true
        : platform == 'desktop' ? true
        : !/Trident\//.test(navigator.userAgent);

    const stringify = value => value instanceof Object ? JSON.stringify(value, Object.keys(value).sort()) : '' + value;
    const escape = json => json
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"');


    /**
     * A dropdown control that uses either a native `select` element or emulation depending on device.
     */
    export default {
        name: 'ib-dropdown',

        mixins: [
            entry
        ],

        props: {
            /**
             * An array of objects, each of which is a list entry with a label and a value.
             * It is possible to nest subentries below an entry by using the `child` property.
             */
            data: {
                type: Array,
                default: () => []
            },
            /**
             * Value of currently selected entry, typically applied with `v-model`.
             */
            value: {
                type: [String, Number, Boolean, Array],
                default: undefined
            },
            /**
             * Member of data entry to emit as value. May be specified as a function.
             * By default, for object entries we emit the `value` member and for non-object entries we emit the entry.
             */
            emit: {
                type: [String, Number, Function],
                default: 'value'
            },
            /**
             * Array of values that should not be selectable, or a function that takes as argument an element from the
             * `data` array and returns a truthy value if the item should not be selectable.
             */
            disableItem: [Array, Function],
            /**
             * Appearance modifier: `frameless` or `underline`. On Android, `underline` is the default.
             */
            appearance: String,
            /**
             * Allow selection of multiple entries; if true, emits an array.
             */
            multiple: Boolean,
            /**
             * Allow filtering of displayed entries via keyboard input.
             */
            filtered: Boolean,
            /**
             * Use a placeholder in the dropdown box when the value is empty. Useful for initial state prompts.
             */
            placeholder: String
        },

        data() {
            return {
                selectedOptions: [],
                filter: null,
                picker: null,
                validity: null,
                expanded: false,
                tagsWidth: 0,
                errorPosition: 'top'
            };
        },

        computed: {
            native() {
                return !$device.keyboard && !this.filtered;
            },

            _id() {
                return this.id || '_dd' + this._uid;
            },

            _iid() {
                return '_ddi' + this._uid;
            },

            _class() {
                return {
                    _fo: this.focused,
                    _mu: this.multiple,
                    _fi: this.filtered,
                    _da: this.disabled,
                    _inv: this.showvalid && !this.valid,
                    ['_fa'+this.align]: this.align,
                    ['_de'+this.appearance]: this.appearance
                };
            },

            getval() {
                const emit = this.emit;
                return typeof emit === 'function'
                    ? emit
                    : o => typeof o === 'object' ? o[emit] : o;
            },

            attval() {
                return this.native
                    ? o => o.value
                    : o => o.getAttribute('data-value');
            },

            getdisabled() {
                const disableItem = this.disableItem;
                const fn = Array.isArray(disableItem)
                    ? o => disableItem.includes(this.getval(o))
                    : typeof disableItem == 'function'
                        ? disableItem
                        : o => false;
                return this.multiple && !this.native
                    ? o => fn(o) || Array.isArray(this.value) ? this.value.includes(this.getval(o)) : this.value === this.getval(o)
                    : fn;
            },

            dock() {
                if (this.filtered && this.filter) {
                    return true;
                } else {
                    return this.selectedOptions.length;
                }
            }
        },

        watch: {
            data() {
                this.$nextTick(this._sync);
            }
        },

        methods: {
            // Filter box input event
            input() {
                if (!this.picker || !this.picker.visible) {
                    this._popen();
                } else {
                    this.picker.setFilter(this.filter);
                }
            },

            // Emulated picker uses filter to display current value
            syncFilter() {
                this.filter = !this.multiple && this.selectedOptions.length ? this.selectedOptions[0].textContent : null;
            },

            // On data or value update and emulated picker, recalculate `this.selectedOptions`
            syncEmu() {
                const value = this.ivalue;
                const sel = value => '[data-value="' + escape(stringify(value)) + '"]';
                let selectedOptions;
                if (this.multiple && value && value.map && value.length) {
                    const node = this.$refs.ul.querySelectorAll(value.map(sel));
                    selectedOptions = [].slice.call(node);
                } else {
                    const node = value == null ? value : this.$refs.ul.querySelector(sel(value));
                    selectedOptions = node ? [node] : [];
                }
                this.selectedOptions = selectedOptions;
                this.syncFilter();
            },

            // On data or value update or user input and native picker, recalculate `this.selectedOptions`
            syncNative() {
                this.selectedOptions = haveSelectedOptions($build.platform)
                    ? [].slice.call(this.$refs._in.selectedOptions)
                    : [].filter.call(this.$refs._in.options, option => option.selected);
            },

            // Picker (native or emulated) change event
            _change() {
                if (this.native) {
                    this.syncNative();
                }
                this._set(this.multiple
                    ? [].map.call(this.selectedOptions, this.attval)
                    : this.attval(this.selectedOptions[0]));
                this.validate();
                this.$emit('change', this.ivalue);
            },

            /**
             * Forward Enter/KeyDown/KeyUp/Backspace to emulated picker
             * @private
             */
            _keydown(e) {
                if (!this.native) {
                    switch (e.which) {
                    case 13:
                        if (this.picker && this.picker.visible && this.multiple) {
                            this.filter = null;
                        }
                        // fall through...

                    case 40:
                        if (!this.picker || !this.picker.visible || this.picker.owner !== this) {
                            this._popen();
                        }
                        // fall through...

                    case 27:
                    case 33:
                    case 34:
                    case 38:
                        if (this.picker && this.picker.visible) {
                            this.picker.key(e.keyCode);
                            e.preventDefault();
                            e.stopPropagation();
                        }
                        break;

                    case 8:
                        if (this.multiple) {
                            const input = this.$refs._in;
                            if (!input.selectionStart && !input.selectionEnd) {
                                const selectedOptions = this.selectedOptions;
                                if (selectedOptions.length) {
                                    this._rmtag(selectedOptions[selectedOptions.length - 1]);
                                    e.preventDefault();
                                }
                            }
                        }
                        break;
                    }
                }
                e.stopPropagation();
            },

            /** @private */
            _mousedown(e) {
                if (!e.button && !this.native && !this.readonly) {
                    if (!this.picker || !this.picker.visible) {
                        this._popen();
                    } else {
                        this._pclose();
                    }
                    // stopPropagation without preventDefault causes misbehavior in Safari here
                    e.preventDefault();
                    e.stopPropagation();
                    this.$refs._in.focus();
                }
            },

            /** @private */
            _focus(e) {
                if (this.filtered) {
                    this.$refs._in.select();
                }
                this.focused = true;
            },

            /** @private */
            _blur(e) {
                this._pclose();
                if (!this.multiple && this.filtered && this.ivalue && this.filter != (this.selectedOptions[0] || {}).textContent) {
                    this._set(null);
                }
                this.focused = false;
            },

            /**
             * Sync DOM on data or value update.
             * @private
             */
            _sync() {
                if (this.native) {
                    const input = this.$refs._in;
                    const value = this.ivalue;
                    if (this.multiple && Array.isArray(value)) {
                        const v = value.map(e => stringify(e));
                        const options = input.options;
                        for (let i = 0; i < options.length; i++) {
                            options[i].selected = v.indexOf(options[i].value) >= 0;
                        }
                    } else {
                        input.value = value == null ? value : stringify(value);
                    }
                    this.syncNative();
                } else {
                    this._pclose();
                    this.syncEmu();
                }
                this.validate();
            },

            /**
             * Open the picker.
             * @private
             */
            _popen() {
                this.picker = this.$overlay(picker, {
                    owner: this,
                    anchor: this.$el,
                    value: this.ivalue,
                    filter: this.filtered ? this.filter : '',
                    align: 'grow',
                    _style: {
                        fontSize: this.$util.css(this.$el, 'font-size')
                    }
                });
                this.picker.open();
            },

            /**
             * Close the picker.
             * @private
             */
            _pclose() {
                if (this.picker && this.picker.owner === this) {
                    this.picker.close();
                }
            },

            /**
             * Remove a tag from a multiple select dropdown.
             * @private
             */
            _rmtag(option) {
                const value = this.native ? option.value : option.getAttribute('data-value');
                const valueCopy = this.ivalue.slice ? this.ivalue.slice() : [];
                const filtered = valueCopy.filter(v => v != JSON.stringify(value) && v != value);
                this._set(filtered);
            },

            /**
             * Custom validation, due to emulated component.
             * @private
             */
            validate() {
                let validity = null;
                if (this.error) {
                    validity = this.error;
                } else if (this.required && !this.selectedOptions.length) {
                    validity = this.errorMissing ||
                        (this.multiple ? this.$locale.data.selMissingMul : this.$locale.data.selMissing);
                }
                this.validity = validity;
                this.valid = !validity;
                this.__refresherror();
                return this.valid;
            },

            getvalidity() {
                return this.validity;
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._drop {
        position: relative;
        display: inline-flex;
        flex-direction: column;
        align-items: stretch;
        .font-size(@fs6);
        font-weight: 400;
        margin: 2px 3px;
        color: @fg;
        background-color: @bg-field;
        outline: none;
        width: auto;
        cursor: default;
        .dropdown-default();

        &    { .field-padding(4px);  }
        &.xs { .field-padding(0);    }
        &.sm { .field-padding(2px);  }
        &.lg { .field-padding(8px);  }
        &.xl { .field-padding(16px); }

        > ._fldi {
            border-color: inherit;
        }
        &._da > ._fldi {
            opacity: .5;
        }
        input {
            width: 0;
        }
        ._fldis {
            overflow: hidden;
        }
        ._fldl {
            -webkit-padding-end: @dropdown-arrow-width - 4px;
            padding-inline-end: @dropdown-arrow-width - 4px;
        }

        /** Multiple select */
        &._mu {
            flex-direction: row;
            flex-wrap: wrap;
            width: 24ch;
            height: auto;
            .field-padding(2px);
            ._fldi {
                margin-top: 2px;
                margin-bottom: 2px;
            }
            ._fldin {
                min-width: 4ch;
            }
            ._fldis {
                min-width: 12px;
            }
        }
    }

    ._derounded     { .dropdown-rounded(); }
    ._deframeless   { .dropdown-frameless(); }

    /** Picker list (only while docked in control) */
    ._drop > ._ddl {
        width: 100%;
        padding-right: @dropdown-arrow-width; /* Space for arrow */
        line-height: 0;
        height: 0;
        visibility: hidden;
        > li {
            height: 0;
            padding: 0;
        }
    }
    ._deframeless > ._ddl {
        display: none;
    }

    /** Picker list (whether docked in control or overlay) */
    ._ddl {
        list-style: none;
        margin: 0;
        padding: 0;
        white-space: nowrap;
        > li {
            margin: 0;
            padding: 0 8px;
            &:not([role=option]) {
                font-weight: 600;
            }
        }
    }

    /** Picker overlay */
    ._d2 {
        background-color: @bg-dialog;
        z-index: @z-popover;

        & {
            transform-origin: top;
        } &._ptop {
            transform-origin: bottom;
        }
        & {
            transform: scaleY(.01);
        } &._v {
            transform: scaleY(1);
        }
    }

    ._ddo {
        width: 100%;
        max-height: 280px;
        overflow-x: hidden;
        overflow-y: auto;
        cursor: default;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;

        li {
            .line-height(28);
            &._foc {
                background: @bg-select;
                color: @fg-select;
            }
            &[aria-disabled=true] {
                opacity: .25;
            }
        }
    }

    [dir=rtl] ._drop {
        &._acenter ._fldl,
        &._aend ._fldl {
            padding: 0 0 0 20px;
        }
    }

    /** Native picker - TODO:REMOVE */
    select._fldin {
        &._dock,
        &[inert] {
            visibility: hidden;
        }
        &[multiple] {
            color: transparent;
        }
        &:-moz-focusring {
            color: transparent !important;
            text-shadow: 0 0 0 @fg;
            option:disabled {
                text-shadow: none;
            }
        }
    }

    ._fldis {
        flex: 1 1 auto;
        justify-content: inherit;
        display: inherit;
        outline: none;

        &._dock {
            /** visibility: hidden would prevent focus */
            opacity: 0;
        }
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-dropdown(:data="data", v-model="value")
    </template>
    <script>
        {
            data() {
                return {
                    data: ["One","Two","Three","Four","Five","Six","Seven","Eight"],
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="filtered" name="Filtered">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-dropdown(:data="data", v-model="value", filtered)
    </template>
    <script>
        {
            data() {
                return {
                    data: ["One","Two","Three","Four","Five","Six","Seven","Eight"],
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="disabled" name="Disabled Items">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-dropdown(:data="data", :disableItem="disableItem", v-model="value")
    </template>
    <script>
        {
            data() {
                return {
                    data: ["One","Two","Three","Four","Five","Six","Seven","Eight"],
                    disableItem: ["Two","Four","Five"],
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="multiple" name="Multiple Select">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-dropdown(:data="data", v-model="value", multiple)
    </template>
    <script>
        {
            data() {
                return {
                    data: ["One","Two","Three","Four","Five","Six","Seven","Eight"],
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="multiple-filtered" name="Multiple, Filtered">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-dropdown(:data="data", v-model="value", multiple, filtered)
    </template>
    <script>
        {
            data() {
                return {
                    data: ["One","Two","Three","Four","Five","Six","Seven","Eight"],
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="align" name="Alignment">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-dropdown(width="24ch", :data="data", v-model="value", placeholder="start", align="start")
            .before-4
                ib-dropdown(width="24ch", :data="data", v-model="value", placeholder="center", align="center")
            .before-4
                ib-dropdown(width="24ch", :data="data", v-model="value", placeholder="end", align="end")
    </template>
    <script>
        {
            data() {
                return {
                    data: ["One","Two","Three","Four","Five","Six","Seven","Eight"],
                    value: null
                };
            }
        }
    </script>
</ib-example>
